import * as THREE from "three";
import { TANK_CONFIG, ENVIRONMENT_CONFIG } from "../config";
import {
  Environment,
  isCollidingWithRocks,
  isCollidingWithTrees,
  breakTree,
  checkTankCollisions,
  isInMud,
} from "../environment";
import { Tank } from "../Tank";
import { TireTrail } from "../TireTrail";
import { handleTankCollision } from "../damage";

/**
 * Interfejs kontrolek ruchu czołgu
 */
export interface MovementControls {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  handbrake: boolean;
  barrelUp: boolean;
  barrelDown: boolean;
}

/**
 * Kontroler odpowiedzialny za obsługę ruchu czołgu
 * Zawiera logikę poruszania się, kolizji oraz efektów wizualnych związanych z ruchem
 */
export class TankMovementController {
  // Parametry ruchu publiczne dla Tank
  public maxSpeed: number;
  public maxSpeedKMH: number;
  public speedFactor: number;
  public acceleration: number;
  public deceleration: number;
  public turnSpeed: number;
  public maxTurnSpeed: number;
  public currentSpeed: number = 0;
  public currentTurnSpeed: number = 0;
  public displaySpeed: number = 0;
  private isBoosting: boolean = false;

  // Komponenty czołgu
  public mesh: THREE.Object3D;
  public scene: THREE.Scene;
  public brakeMarks: THREE.Mesh[] = [];
  private wheels: THREE.Mesh[];
  private trails: TireTrail[] = [];
  private tank: Tank;
  private leftTrackPosition: THREE.Vector3 = new THREE.Vector3();
  private rightTrackPosition: THREE.Vector3 = new THREE.Vector3();
  private moveDirection: THREE.Vector3 = new THREE.Vector3();
  private nextPosition: THREE.Vector3 = new THREE.Vector3();
  private oldPosition: THREE.Vector3 = new THREE.Vector3();
  private rockToTank: THREE.Vector3 = new THREE.Vector3();
  private actualMoveVector: THREE.Vector3 = new THREE.Vector3();
  private pushbackPosition: THREE.Vector3 = new THREE.Vector3();
  private directionToObject: THREE.Vector3 = new THREE.Vector3();
  private trailOffset: THREE.Vector3 = new THREE.Vector3();

  // Stan kontrolek
  private controls: MovementControls = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    handbrake: false,
    barrelUp: false,
    barrelDown: false,
  };

  // Środowisko i kolizje
  private environment: Environment | null = null;

  // Aktualizacje sieciowe
  private lastSentPosition: THREE.Vector3 | null = null;
  private lastSentRotation: number | null = null;
  private lastSentTurretRotation: number | null = null;
  private lastNetworkUpdateTime: number = 0;
  private readonly POSITION_UPDATE_THRESHOLD = 0.01;
  private readonly ROTATION_UPDATE_THRESHOLD = 0.01;
  private readonly NETWORK_UPDATE_INTERVAL = ENVIRONMENT_CONFIG.UPDATE_THROTTLE;

  // [NEW] Shared geometry for brake marks
  private static brakeMarkGeometry = new THREE.PlaneGeometry(0.2, 0.4);

  constructor(scene: THREE.Scene, tank: Tank, wheels: THREE.Mesh[]) {
    this.scene = scene;
    this.tank = tank;
    this.mesh = tank.mesh;
    this.wheels = wheels;

    // Inicjalizacja śladów gąsienic
    this.initializeTrails();

    // Pobierz parametry ruchu z obiektu Tank zamiast z konfiguracji
    // To pozwoli na zastosowanie modyfikatorów z wybranego modelu czołgu
    this.maxSpeed = tank.maxSpeed;
    this.maxSpeedKMH = tank.maxSpeedKMH;
    this.acceleration = tank.acceleration;
    this.deceleration = TANK_CONFIG.MOVEMENT.DECELERATION; // Zachowujemy domyślną wartość, bo nie jest modyfikowana w modelu
    this.turnSpeed = tank.turnSpeed;
    this.maxTurnSpeed = TANK_CONFIG.MOVEMENT.MAX_TURN_SPEED;
    this.speedFactor = tank.speedFactor;

    this.currentSpeed = 0;
    this.currentTurnSpeed = 0;
    this.displaySpeed = 0;
  }

  /**
   * Inicjalizuje ślady gąsienic
   */
  private initializeTrails(): void {
    // Stwórz ślady dla obu gąsienic
    for (let i = 0; i < 2; i++) {
      const trail = new TireTrail();
      this.trails.push(trail);
      this.scene.add(trail.getMesh());
    }
  }

  setEnvironment(environment: Environment): void {
    if (!environment.rocks || !environment.trees) {
      console.warn(`[TankMovementController] Environment for tank ${this.tank.playerId} is missing rocks or trees!`);
    }

    this.environment = environment;
  }

  // Metody kontroli ruchu
  setControl(control: string, value: boolean): void {
    switch (control) {
      case "forward":
        this.controls.forward = value;
        break;
      case "backward":
        this.controls.backward = value;
        break;
      case "left":
        this.controls.left = value;
        break;
      case "right":
        this.controls.right = value;
        break;
      case "handbrake":
        this.controls.handbrake = value;
        break;
      case "barrelUp":
        this.controls.barrelUp = value;
        break;
      case "barrelDown":
        this.controls.barrelDown = value;
        break;
    }
  }

  setBoost(value: boolean): void {
    this.isBoosting = value;
  }

  setHandbrakeState(enabled: boolean): void {
    this.controls.handbrake = enabled;

    // Jeśli włączamy hamulec i nie mamy jeszcze śladów gąsienic, stwórz je
    if (enabled && (!this.trails || this.trails.length === 0)) {
      // Stwórz ślady dla obu gąsienic
      for (let i = 0; i < 2; i++) {
        const trail = new TireTrail();
        this.trails.push(trail);
        this.scene.add(trail.getMesh());
      }
    }
  }

  // Gettery dla stanu ruchu
  getSpeed(deltaTime: number): number {
    // Płynnie dostosuj wyświetlaną prędkość do rzeczywistej prędkości
    const speedDiff = this.currentSpeed - this.displaySpeed;

    // Szybsze dostosowanie wskaźnika prędkości do rzeczywistej prędkości
    const baseAdaptationRate = 2.0; // Zwiększona szybkość dostosowania
    const minAdaptationRate = 0.8; // Zwiększona minimalna szybkość dostosowania

    // Adaptacja zależy od aktualnej prędkości - im wyższa prędkość, tym szybsza adaptacja
    const speedAdaptationFactor = Math.abs(this.currentSpeed) / this.maxSpeed;
    const adaptationRate = minAdaptationRate + (baseAdaptationRate - minAdaptationRate) * speedAdaptationFactor;

    // Oblicz zmianę prędkości wyświetlanej
    const displaySpeedChange = speedDiff * adaptationRate * deltaTime;

    // Zastosuj zmianę z ograniczeniem
    this.displaySpeed += displaySpeedChange;

    // Dodaj losowe drgania wskaźnika przy wyższych prędkościach
    if (Math.abs(this.currentSpeed) > 0.05) {
      const vibration = (Math.random() - 0.5) * 0.005 * Math.abs(this.currentSpeed);
      this.displaySpeed += vibration;
    }

    // Upewnij się, że wyświetlana prędkość nie przekracza rzeczywistej o zbyt dużo
    // Wskaźnik może pokazywać nieco więcej niż rzeczywista prędkość, ale nie mniej
    if (Math.abs(this.displaySpeed) > Math.abs(this.currentSpeed) * 1.1) {
      this.displaySpeed = Math.sign(this.displaySpeed) * Math.abs(this.currentSpeed) * 1.1;
    }
    return this.displaySpeed;
    //return this.currentSpeed;
  }

  getMaxSpeed(): number {
    return this.maxSpeed;
  }

  getDisplaySpeed(): number {
    //return this.displaySpeed;
    return Math.abs(this.displaySpeed) * this.tank.speedFactor;
  }

  setDisplaySpeed(value: number): void {
    this.displaySpeed = value;
  }

  startMovingForward(): void {
    this.controls.forward = true;
  }

  stopMovingForward(): void {
    this.controls.forward = false;
  }

  startMovingBackward(): void {
    this.controls.backward = true;
  }

  stopMovingBackward(): void {
    this.controls.backward = false;
  }

  startTurningLeft(): void {
    this.controls.left = true;
  }

  stopTurningLeft(): void {
    this.controls.left = false;
  }

  startTurningRight(): void {
    this.controls.right = true;
  }

  stopTurningRight(): void {
    this.controls.right = false;
  }

  // Główna metoda aktualizacji
  update(deltaTime: number, isDestroyed: boolean): void {
    if (isDestroyed) {
      this.resetMovement();
      return;
    }

    this.updateMovement(deltaTime, isDestroyed);
    this.updateWheels(deltaTime);
    this.updateTrails();
    this.updateBrakeMarks();
  }

  // Aktualizacja ruchu czołgu
  updateMovement(deltaTime: number, isDestroyed: boolean): void {
    if (isDestroyed) {
      this.resetMovement();
      return;
    }

    // Sprawdź czy czołg jest w błocie
    if (this.environment && isInMud(this.mesh.position, this.environment.mudPatches)) {
      // Zmniejsz maksymalną prędkość w błocie - używamy wartości z modelu czołgu
      const mudSpeedMultiplier = ENVIRONMENT_CONFIG.MUD.SPEED_MULTIPLIER;
      this.maxSpeed = this.tank.maxSpeed * mudSpeedMultiplier;
      this.maxSpeedKMH = this.tank.maxSpeedKMH * mudSpeedMultiplier;

      // Dodatkowe spowolnienie w błocie
      //const mudSlowdown = 0.7; // 30% wolniej
      //deltaTime *= mudSlowdown;
    } else {
      // Normalna maksymalna prędkość - używamy wartości z modelu czołgu
      this.maxSpeed = this.tank.maxSpeed;
      this.maxSpeedKMH = this.tank.maxSpeedKMH;
    }

    this.updateSpeed(deltaTime);
    this.handleTurning(deltaTime);
    this.handleHandbrake(deltaTime);
    this.updatePositionAndCollisions(deltaTime);
  }

  /**
   * Metoda publiczna do resetowania ruchu
   */
  public resetMovement(): void {
    this.currentSpeed = 0;
    this.currentTurnSpeed = 0;
    this.displaySpeed = 0;
    this.isBoosting = false;
    this.controls = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      handbrake: false,
      barrelUp: false,
      barrelDown: false,
    };
  }

  private updateSpeed(deltaTime: number): void {
    const originalSpeed = this.currentSpeed;

    // Aktualizacja prędkości
    if (this.controls.forward) {
      // Efekt trudniejszego przyspieszania przy wyższych prędkościach
      const accelerationFactor = 1 - (this.currentSpeed / this.maxSpeed) * 0.3;

      // Mniejsze opóźnienie w reakcji na sterowanie - czołg reaguje szybciej
      const startupDelay = Math.max(0, 0.3 - Math.abs(this.currentSpeed) * 5);
      const effectiveAcceleration = this.acceleration * accelerationFactor * (1 - startupDelay);
      this.currentSpeed += effectiveAcceleration * deltaTime;

      if (this.isBoosting) {
        // Dodatkowe przyspieszenie przy booście
        this.currentSpeed += this.acceleration * 0.5 * deltaTime;
      }
    } else if (this.controls.backward) {
      // Efekt trudniejszego przyspieszania przy wyższych prędkościach
      const accelerationFactor = 1 - (Math.abs(this.currentSpeed) / this.maxSpeed) * 0.3;

      // Mniejsze opóźnienie w reakcji na sterowanie - czołg reaguje szybciej
      const startupDelay = Math.max(0, 0.3 - Math.abs(this.currentSpeed) * 5);
      const effectiveAcceleration = this.acceleration * accelerationFactor * 0.6 * (1 - startupDelay);

      // Wolniejsze przyspieszanie do tyłu
      this.currentSpeed -= effectiveAcceleration * deltaTime;
    } else {
      // Hamowanie
      this.handleDeceleration(deltaTime);
    }

    // Ograniczenie prędkości
    const actualMaxSpeed = this.isBoosting ? this.maxSpeed * 1.3 : this.maxSpeed;
    this.currentSpeed = THREE.MathUtils.clamp(
      this.currentSpeed,
      -actualMaxSpeed * 0.6, // Wolniejsze cofanie
      actualMaxSpeed,
    );
  }

  private handleDeceleration(deltaTime: number): void {
    // Hamowanie
    if (this.currentSpeed > 0) {
      // Wolniejsze hamowanie przy wyższych prędkościach - efekt bezwładności
      const inertiaFactor = 1 + (this.currentSpeed / this.maxSpeed) * 0.5;
      this.currentSpeed -= (this.deceleration / inertiaFactor) * deltaTime;
    } else if (this.currentSpeed < 0) {
      // Wolniejsze hamowanie przy wyższych prędkościach - efekt bezwładności
      const inertiaFactor = 1 + (Math.abs(this.currentSpeed) / this.maxSpeed) * 0.5;
      this.currentSpeed += (this.deceleration / inertiaFactor) * deltaTime;
    }

    // Zatrzymaj całkowicie, jeśli prędkość jest bardzo mała
    if (Math.abs(this.currentSpeed) < this.deceleration * deltaTime) {
      this.currentSpeed = 0;
    }
  }

  // Skręcanie
  private handleTurning(deltaTime: number): void {
    if (this.controls.left) {
      this.mesh.rotation.y += this.turnSpeed * (Math.abs(this.currentSpeed) > 0.01 ? 1 : 0.5) * deltaTime;
    } else if (this.controls.right) {
      this.mesh.rotation.y -= this.turnSpeed * (Math.abs(this.currentSpeed) > 0.01 ? 1 : 0.5) * deltaTime;
    }
  }

  private handleHandbrake(deltaTime: number): void {
    // Hamulec ręczny
    if (this.controls.handbrake) {
      // Szybsze hamowanie
      const handbrakeForce = TANK_CONFIG.MOVEMENT.HANDBRAKE_DECELERATION * deltaTime;

      // Zachowaj oryginalną prędkość do sprawdzania tworzenia śladów
      const originalSpeed = Math.abs(this.currentSpeed);

      // Hamuj mocniej, gdy jedziemy szybciej
      const speedBrakingFactor = Math.min(3.0, 1.0 + Math.abs(this.currentSpeed) * 5.0);

      // Zastosuj hamowanie
      if (this.currentSpeed > 0) {
        this.currentSpeed = Math.max(0, this.currentSpeed - handbrakeForce * speedBrakingFactor);
      } else if (this.currentSpeed < 0) {
        this.currentSpeed = Math.min(0, this.currentSpeed + handbrakeForce * speedBrakingFactor);
      }

      // Zwiększona prędkość skrętu podczas hamulca ręcznego
      let isTurning = false;
      if (this.controls.left) {
        this.mesh.rotation.y += this.turnSpeed * TANK_CONFIG.MOVEMENT.HANDBRAKE_TURN_BOOST * deltaTime;
        isTurning = true;
      }
      if (this.controls.right) {
        this.mesh.rotation.y -= this.turnSpeed * TANK_CONFIG.MOVEMENT.HANDBRAKE_TURN_BOOST * deltaTime;
        isTurning = true;
      }

      // Twórz ślady hamowania tylko jeśli:
      // 1. Czołg porusza się z odpowiednią prędkością początkową
      // 2. Używamy hamulca ręcznego podczas skręcania LUB hamujemy z dużej prędkości
      if (
        this.environment &&
        this.environment.scene &&
        originalSpeed > TANK_CONFIG.MOVEMENT.MIN_SPEED_FOR_TRAILS &&
        (isTurning || originalSpeed > TANK_CONFIG.MOVEMENT.MIN_SPEED_FOR_HANDBRAKE_TRAILS)
      ) {
        // Twórz ślady z obu stron czołgu
        const leftTrailPos = this.getTrailPosition(-1, this.leftTrackPosition);
        const rightTrailPos = this.getTrailPosition(1, this.rightTrackPosition);

        // Twórz ślady hamowania
        this.createBrakeMark(leftTrailPos);
        this.createBrakeMark(rightTrailPos);

        // Usuń stare ślady jeśli jest ich za dużo
        while (this.brakeMarks.length > TANK_CONFIG.MOVEMENT.MAX_BRAKE_MARKS) {
          const oldMark = this.brakeMarks.shift();
          if (oldMark && this.environment?.scene) {
            this.environment.scene.remove(oldMark);
            (oldMark.material as THREE.MeshBasicMaterial).dispose();
          }
        }
      }
    }
  }

  private getTrailPosition(side: number, target: THREE.Vector3): THREE.Vector3 {
    this.trailOffset.set((side * TANK_CONFIG.MOVEMENT.TRACK_WIDTH) / 2, 0.01, 0);
    this.trailOffset.applyQuaternion(this.mesh.quaternion);
    return target.copy(this.mesh.position).add(this.trailOffset);
  }

  private createBrakeMark(position: THREE.Vector3): void {
    if (!this.environment || !this.environment.scene) return;

    // Stwórz geometrię śladu - prostokąt
    // [CHANGED] Use shared geometry
    const geometry = TankMovementController.brakeMarkGeometry;

    // Materiał z przezroczystością i zanikaniem
    const material = new THREE.MeshBasicMaterial({
      color: 0x332211,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mark = new THREE.Mesh(geometry, material);

    // Ustaw pozycję i rotację
    mark.position.copy(position);
    mark.rotation.x = -Math.PI / 2; // Połóż płasko na ziemi
    mark.rotation.z = this.mesh.rotation.y; // Dopasuj do kierunku czołgu

    // Dodaj do sceny i listy śladów (zanikanie w updateBrakeMarks - jeden loop zamiast rAF per ślad)
    mark.userData.birthTime = Date.now();
    this.environment.scene.add(mark);
    this.brakeMarks.push(mark);
  }

  // Perf: centralne zanikanie śladów raz na klatkę zamiast osobnego
  // requestAnimationFrame per ślad (do 100 równoległych loopów). Ten sam wygląd.
  private updateBrakeMarks(): void {
    if (this.brakeMarks.length === 0) {
      return;
    }
    const now = Date.now();
    for (let i = this.brakeMarks.length - 1; i >= 0; i--) {
      const mark = this.brakeMarks[i];
      const elapsedTime = now - (mark.userData.birthTime as number);
      const fadeProgress = elapsedTime / TANK_CONFIG.MOVEMENT.TRAIL_FADE_TIME;

      if (fadeProgress < 1) {
        (mark.material as THREE.MeshBasicMaterial).opacity = 0.7 * (1 - fadeProgress);
      } else {
        this.environment?.scene?.remove(mark);
        (mark.material as THREE.MeshBasicMaterial).dispose();
        this.brakeMarks.splice(i, 1);
      }
    }
  }

  private updatePositionAndCollisions(deltaTime: number): void {
    // Oblicz wektor kierunku ruchu
    this.moveDirection.set(0, 0, 1).applyQuaternion(this.mesh.quaternion).normalize();

    const newPosition = this.nextPosition
      .copy(this.mesh.position)
      .addScaledVector(this.moveDirection, this.currentSpeed * deltaTime);

    // Sprawdzenie kolizji przed aktualizacją pozycji
    if (this.checkCollisions(newPosition)) {
      // Przy kolizji zmniejsz prędkość zgodnie z konfiguracją
      this.currentSpeed *= ENVIRONMENT_CONFIG.COLLISION.SPEED_REDUCTION_ON_COLLISION;
      return;
    }

    // Aktualizuj pozycję czołgu
    this.mesh.position.copy(newPosition);

    // Sprawdź czy trzeba wysłać aktualizację pozycji
    this.checkNetworkUpdate();
  }

  private checkCollisions(newPosition: THREE.Vector3): boolean {
    if (!this.environment) {
      return false;
    }

    // Zapisz starą pozycję do sprawdzania kierunku ruchu
    const oldPosition = this.oldPosition.copy(this.mesh.position);
    const moveDirection = this.moveDirection
      .set(0, 0, this.currentSpeed > 0 ? 1 : -1)
      .applyQuaternion(this.mesh.quaternion)
      .normalize();

    // Kolizje z kamieniami
    if (isCollidingWithRocks(newPosition, this.environment.rocks)) {
      // Znajdź kamień, z którym nastąpiła kolizja
      const collidingRock = this.getCollidingRock(newPosition);

      if (collidingRock) {
        // Oblicz wektor od kamienia do czołgu
        const rockToTank = this.rockToTank.subVectors(oldPosition, collidingRock.position);
        rockToTank.y = 0; // Ignoruj wysokość
        rockToTank.normalize();

        // Oblicz wektor aktualnego ruchu (niezależnie od orientacji czołgu)
        const actualMoveVector = this.actualMoveVector.subVectors(newPosition, oldPosition).normalize();
        const dotProduct = actualMoveVector.dot(rockToTank);

        // Sprawdź, czy czołg próbuje się cofnąć od kamienia
        const movingAwayFromRock = dotProduct > 0;

        if (!movingAwayFromRock) {
          // Kolizja z kamieniem - zatrzymaj czołg tylko jeśli nie cofa się od kamienia

          // Zastosuj siłę odbicia od kamienia
          const bounceForce = ENVIRONMENT_CONFIG.COLLISION.COLLISION_BOUNCE_FACTOR;
          this.currentSpeed *= -bounceForce; // Odbij się w przeciwnym kierunku

          // Dodaj lekkie przesunięcie w kierunku od kamienia aby zapobiec zaklinowaniu
          const pushbackDistance = ENVIRONMENT_CONFIG.COLLISION.PUSHBACK_STRENGTH;
          const pushbackPosition = this.pushbackPosition.copy(oldPosition).addScaledVector(rockToTank, pushbackDistance);
          this.mesh.position.copy(pushbackPosition);

          return true;
        } else {
          return false;
        }
      }
    }

    // Kolizje z drzewami
    if (this.environment.trees && this.environment.trees.length > 0) {
      const treeCollision = isCollidingWithTrees(newPosition, this.environment.trees);
      if (treeCollision.colliding && treeCollision.tree) {
        const tree = treeCollision.tree;

        // Sprawdź, czy drzewo już zostało złamane
        if (!(tree as any).isBroken) {
          // Złam drzewo tylko jeśli czołg porusza się z wystarczającą prędkością
          const minSpeedToBreakTree = ENVIRONMENT_CONFIG.COLLISION.MIN_SPEED_TO_BREAK_TREE;

          if (Math.abs(this.currentSpeed) >= minSpeedToBreakTree) {
            // Złam drzewo
            if (this.environment.scene) {
              breakTree(tree, this.environment.scene, moveDirection);
            }

            // Zmniejsz prędkość czołgu po uderzeniu w drzewo
            this.currentSpeed *= ENVIRONMENT_CONFIG.COLLISION.SPEED_REDUCTION_ON_TREE_BREAK;
            //some sound?
            return false;
          } else {
            // Prędkość zbyt mała, sprawdź czy czołg cofa się od drzewa
            const movingAwayFromTree = this.isMovingAwayFromObject(oldPosition, moveDirection, tree.position);

            if (!movingAwayFromTree) {
              return true;
            } else {
              return false;
            }
          }
        }
      }
    }

    // Kolizje z innymi czołgami
    if (window.gameState?.tanks) {
      const collidingTanks = checkTankCollisions(this.tank, window.gameState.tanks, false);
      if (collidingTanks.length > 0) {
        // Sprawdź czy czołg cofa się od kolidującego czołgu
        for (const otherTank of collidingTanks) {
          // Jeśli którykolwiek z czołgów jest w strefie respawnu, pomijamy kolizję
          if (this.tank.isInRespawnZone() || otherTank.isInRespawnZone()) {
            continue;
          }

          const movingAwayFromTank = this.isMovingAwayFromObject(oldPosition, moveDirection, otherTank.mesh.position);

          if (!movingAwayFromTank) {
            handleTankCollision(this.tank, otherTank);
            return true;
          }
        }
      }
    }

    return false;
  }

  private getCollidingRock(position: THREE.Vector3): THREE.Mesh | null {
    if (!this.environment) return null;

    const tankRadius = ENVIRONMENT_CONFIG.COLLISION.TANK_RADIUS;

    // Znajdź kamień, z którym nastąpiła kolizja
    for (const rock of this.environment.rocks) {
      // Oblicz rzeczywisty promień kamienia (uwzględniając skalę)
      const rockScale = Math.max(rock.scale.x, rock.scale.z);
      const rockRadius = 0.5 * rockScale; // Bazowy promień * skala

      // Minimalna odległość to suma promieni
      const minDistance = tankRadius + rockRadius;

      // Sprawdź tylko odległość w płaszczyźnie X-Z (ignoruj Y)
      const dx = position.x - rock.position.x;
      const dz = position.z - rock.position.z;
      if (dx * dx + dz * dz < minDistance * minDistance) {
        return rock;
      }
    }
    return null;
  }

  /**
   * Sprawdza, czy czołg porusza się w kierunku przeciwnym do obiektu (oddala się)
   * @param tankPosition Aktualna pozycja czołgu
   * @param moveDirection Kierunek ruchu czołgu
   * @param objectPosition Pozycja obiektu (np. drzewa)
   * @returns true jeśli czołg oddala się od obiektu, false w przeciwnym razie
   */
  private isMovingAwayFromObject(
    tankPosition: THREE.Vector3,
    moveDirection: THREE.Vector3,
    objectPosition: THREE.Vector3,
  ): boolean {
    // Sprawdź, czy czołg porusza się w kierunku przeciwnym do obiektu
    const directionToObject = this.directionToObject.subVectors(tankPosition, objectPosition).normalize();

    // Jeśli kąt między kierunkiem ruchu a kierunkiem do obiektu jest większy niż 90 stopni,
    // to czołg porusza się w kierunku przeciwnym do obiektu
    const angle = Math.acos(directionToObject.dot(moveDirection));

    return angle > Math.PI / 2;
  }

  private updateWheels(deltaTime: number): void {
    const wheelRotationSpeed = this.currentSpeed * 10;
    this.wheels.forEach((wheel) => {
      //wheel.rotation.x += wheelRotationSpeed * deltaTime;
      wheel.rotation.x += this.currentSpeed * deltaTime * 5; // Prosta animacja
    });
  }

  private checkNetworkUpdate() {
    if (this.shouldSendPositionUpdate()) {
      this.sendPositionUpdate();
    }
  }

  public shouldSendPositionUpdate(): boolean {
    // Only send updates for the local player's tank
    if (window.playerTank !== this.tank) {
      return false;
    }

    // Don't send updates for destroyed tanks
    if (this.tank.isDestroyed) {
      return false;
    }

    // Check if enough time has passed since last update
    const now = Date.now();
    if (now - this.lastNetworkUpdateTime < this.NETWORK_UPDATE_INTERVAL) {
      return false;
    }

    // Check if position has changed significantly
    const positionChanged =
      !this.lastSentPosition ||
      this.mesh.position.distanceToSquared(this.lastSentPosition) >
        this.POSITION_UPDATE_THRESHOLD * this.POSITION_UPDATE_THRESHOLD;

    // Check if rotation has changed significantly
    const rotationChanged =
      !this.lastSentRotation ||
      Math.abs(this.mesh.rotation.y - (this.lastSentRotation || 0)) > this.ROTATION_UPDATE_THRESHOLD;

    // Check if turret rotation has changed significantly
    const turretRotationChanged = this.tank.turret
      ? !this.lastSentTurretRotation ||
        Math.abs(this.tank.turret.rotation.y - (this.lastSentTurretRotation || 0)) > this.ROTATION_UPDATE_THRESHOLD
      : false;

    // Send update if either position, rotation or turret rotation changed meaningfully
    const shouldUpdate = Boolean(positionChanged || rotationChanged || turretRotationChanged);

    // Update last network update time if we're going to send an update
    if (shouldUpdate) {
      this.lastNetworkUpdateTime = now;
    }

    return shouldUpdate;
  }

  public sendPositionUpdate(): void {
    if (!window.socket) {
      return;
    }

    // Save current values
    this.lastSentPosition = this.mesh.position.clone();
    this.lastSentRotation = this.mesh.rotation.y;
    this.lastSentTurretRotation = this.tank.turret ? this.tank.turret.rotation.y : 0;

    // Send tank position update using playerId for identification
    window.socket.emit("tankUpdate", {
      playerId: this.tank.playerId,
      position: {
        x: this.mesh.position.x,
        y: this.mesh.position.y,
        z: this.mesh.position.z,
      },
      rotation: this.mesh.rotation.y,
      turretRotation: this.tank.turret ? this.tank.turret.rotation.y : 0,
      timestamp: Date.now(),
      isDestroyed: this.tank.isDestroyed, // Use tank's isDestroyed property
    });

    // Update last network update time
    this.lastNetworkUpdateTime = Date.now();
  }

  updateTrails(): void {
    if (!this.trails || this.trails.length < 2) {
      // Initialize trails if missing.
      this.initializeTrails();
      return;
    }

    // Track positions in world space.
    this.leftTrackPosition.set(-TANK_CONFIG.MOVEMENT.TRACK_WIDTH / 2, 0.05, 0);
    this.leftTrackPosition.applyMatrix4(this.mesh.matrixWorld);

    this.rightTrackPosition.set(TANK_CONFIG.MOVEMENT.TRACK_WIDTH / 2, 0.05, 0);
    this.rightTrackPosition.applyMatrix4(this.mesh.matrixWorld);

    // Update trail points only while moving.
    const isMoving = Math.abs(this.currentSpeed) > TANK_CONFIG.MOVEMENT.MIN_SPEED_FOR_TRAILS;

    if (isMoving) {
      this.trails[0].update(this.leftTrackPosition);
      this.trails[1].update(this.rightTrackPosition);
    }
  }

  // Getter dla kontrolek ruchu
  getControls(): MovementControls {
    return { ...this.controls };
  }
}
